import { useState } from "react";
import { advisors } from "Constants/advisors";
import { Button } from "antd";
import { Star, CheckCircle, ArrowRight } from "lucide-react";
import { useLoginWithLinkedin } from "Features/Auth/hooks/useLoginWithLinkedin";
import { Users, GraduationCap } from "lucide-react";
import LoginModal from "Features/Navbar/LoginModal";

export function HeroSection() {
  const [loginModal, setLoginModal] = useState(false);
  const login = useLoginWithLinkedin();
  const isVisible = true;

  return (
    <section
      id="home"
      className="relative flex items-center bg-gradient-to-br from-background to-muted/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8 animate-slide-in-up">
            <div className="space-y-6">
              <h1 className="md:text-left text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Verified mentors.{" "}
                <span className="text-primary">Real guidance.</span> Faster
                outcomes.
              </h1>

              <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl text-left">
                Join the OneGrad Community as a Professional, Student, or
                Counselor. Set your price, open time slots, and get paid(in
                future) to help students succeed.
              </p>

              <div className="flex items-center space-x-2 text-foreground font-semibold">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>Every community member is verified by OneGrad.</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="large"
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-3 flex-1"
                onClick={() => {
                  setLoginModal(true);
                }}
              >
                Register Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outlined"
                size="large"
                className="text-muted-foreground hover:text-primary text-lg px-8 py-3 flex-1"
                onClick={() => {
                  setLoginModal(true);
                }}
              >
                Login
              </Button>
            </div>

            <div className="text-sm text-muted-foreground">
              <a
                href="#how-it-works"
                className="hover:text-primary transition-colors underline text-base"
              >
                How it works →
              </a>
            </div>

            <LoginModal
              open={loginModal}
              onClose={() => setLoginModal(false)}
              onGoogleLogin={() => {}}
              onLinkedInLogin={() => {
                login();
              }}
            />
          </div>

          {/* Right Column - Scrolling Portraits */}
          <div className="w-full h-full flex justify-center items-center">
            <div className="flex items-center justify-center">
              <div
                className={`relative transition-all duration-1000 delay-300 ${
                  isVisible
                    ? "translate-y-0 opacity-100 scale-100"
                    : "translate-y-10 opacity-0 scale-95"
                }`}
              >
                {/* Grid of People */}
                <div className="grid grid-cols-3 gap-4 max-w-md">
                  {/* Row 1 */}
                  <div
                    className={`relative group transition-all duration-700 delay-500 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[0].image}
                        height={100}
                        width={100}
                        alt={advisors[0].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                        <GraduationCap className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-blue-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Hero
                    </div>
                  </div>

                  <div
                    className={`relative group transition-all duration-700 delay-700 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[1].image}
                        height={100}
                        width={100}
                        alt={advisors[1].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1">
                        <Users className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-green-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      OG Hero
                    </div>
                  </div>

                  <div
                    className={`relative group transition-all duration-700 delay-900 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[2].image}
                        height={100}
                        width={100}
                        alt={advisors[2].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-purple-500 rounded-full p-1">
                        <Star className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-purple-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Counselor
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div
                    className={`relative group transition-all duration-700 delay-1100 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[3].image}
                        alt={advisors[3].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                        <GraduationCap className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-blue-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Hero
                    </div>
                  </div>

                  <div
                    className={`relative group transition-all duration-700 delay-1300 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[4].image}
                        height={100}
                        width={100}
                        alt={advisors[4].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1">
                        <Users className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-green-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      OG Hero
                    </div>
                  </div>

                  <div
                    className={`relative group transition-all duration-700 delay-1500 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[5].image}
                        height={100}
                        width={100}
                        alt={advisors[5].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-purple-500 rounded-full p-1">
                        <Star className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-purple-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Counselor
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div
                    className={`relative group transition-all duration-700 delay-1700 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[6].image}
                        height={100}
                        width={100}
                        alt={advisors[6].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                        <GraduationCap className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-blue-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Hero
                    </div>
                  </div>

                  <div
                    className={`relative group transition-all duration-700 delay-1900 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[7].image}
                        height={100}
                        width={100}
                        alt={advisors[7].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1">
                        <Users className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-green-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      OG Hero
                    </div>
                  </div>

                  <div
                    className={`relative group transition-all duration-700 delay-2100 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={advisors[8].image}
                        height={100}
                        width={100}
                        alt={advisors[8].name}
                        className="w-20 h-20 rounded-full object-cover border-3 border-[#80E280] shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-purple-500 rounded-full p-1">
                        <Star className="h-3 w-3 text-white" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 bg-white rounded-full px-2 py-1 text-xs font-semibold text-purple-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Counselor
                    </div>
                  </div>
                </div>

                {/* Floating connection lines */}
                <div className="absolute inset-0 pointer-events-none">
                  <svg className="w-full h-full" viewBox="0 0 300 300">
                    <defs>
                      <linearGradient
                        id="connectionGradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          stopColor="#80E280"
                          stopOpacity="0.3"
                        />
                        <stop
                          offset="100%"
                          stopColor="#80E280"
                          stopOpacity="0.1"
                        />
                      </linearGradient>
                    </defs>
                    <path
                      d="M50 50 Q150 100 250 50"
                      stroke="url(#connectionGradient)"
                      strokeWidth="2"
                      fill="none"
                      className={`transition-all duration-1000 delay-2300 ${
                        isVisible ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <path
                      d="M50 150 Q150 200 250 150"
                      stroke="url(#connectionGradient)"
                      strokeWidth="2"
                      fill="none"
                      className={`transition-all duration-1000 delay-2500 ${
                        isVisible ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </svg>
                </div>

                {/* Connection indicator */}
                <div
                  className={`absolute -bottom-10 -left-10 bg-[#80E280] rounded-lg p-3 shadow-lg transition-all duration-700 delay-2900 ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-4 opacity-0"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                    <span className="text-base font-medium text-black">
                      Live Now
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
